import {
  EditActionsDictionary,
  HostEditActionDefinition,
  NativeActionNames,
  IActionPanelAPI,
  ICustomisationAPI,
} from '@wix/editor-platform-host-integration-apis';
import { isEqual } from 'lodash';
import {
  CustomEditActionsDictionary,
  CustomManifestAction,
  PlatformContext,
  CustomizedManifestAction,
} from '../types';
import { isNativeActionName } from '../utils';
import { createCustomisationAPI } from './customisationAPI';
import { createActionPanelAPI } from '../actionPanelAPI';
import {
  createEditorPlatformEditActionsError,
  EditorPlatformEditActionsErrorCode,
} from '../errors';
import { NativeActionBuilder } from '../nativeActions';
import { ActionContext } from '../ActionContext';

export class ForwardAction {
  constructor(
    protected action: CustomManifestAction,
    protected actionName: string,
    private readonly context: PlatformContext,
  ) {}

  private validateAction(): asserts this is this & {
    action: {
      execution: { forward: { elementPath: string; actionName: string } };
    };
  } {
    const { actionName, elementPath } = this.action.execution.forward || {};

    if (!actionName || !elementPath) {
      throw createEditorPlatformEditActionsError(
        EditorPlatformEditActionsErrorCode.EditActionForwardParamsAreMissing,
        `Missing required fields: actionName - ${actionName}, elementPath - ${elementPath}`,
      );
    }
  }

  private getNativeActionTarget(
    actionName: NativeActionNames,
    editorPointer: PlatformContext['editorPointer'],
    targetContext: PlatformContext,
  ) {
    const editActions = this.context.componentsAPI.components.getEditActions(
      editorPointer,
    ) as EditActionsDictionary<CustomManifestAction>;

    const actionDefinition = editActions[actionName];

    if (!actionDefinition) {
      throw createEditorPlatformEditActionsError(
        EditorPlatformEditActionsErrorCode.EditActionForwardActionNotFound,
        `Forwarded native action not found: ${actionName}`,
      ).withActionName(this.actionName);
    }

    const actionContext = new ActionContext(targetContext);
    const builder = new NativeActionBuilder(actionContext);
    const builtAction = builder.build(actionName, actionDefinition);

    if (!builtAction) {
      throw createEditorPlatformEditActionsError(
        EditorPlatformEditActionsErrorCode.EditActionForwardActionNotFound,
        `Native action factory failed to build action: ${actionName}`,
      ).withActionName(this.actionName);
    }

    return {
      action: { ...actionDefinition, ...builtAction },
      actionName,
    };
  }

  private getCustomActionTarget(
    actionName: string,
    editorPointer: PlatformContext['editorPointer'],
  ) {
    const actions = (this.context.componentsAPI.components.getCustomActions(
      editorPointer,
    ) || {}) as CustomEditActionsDictionary<CustomManifestAction>;

    const action = actions[actionName];

    if (!action) {
      throw createEditorPlatformEditActionsError(
        EditorPlatformEditActionsErrorCode.EditActionForwardActionNotFound,
        `Forwarded custom action not found: ${actionName}`,
      ).withActionName(this.actionName);
    }

    return {
      action,
      actionName,
    };
  }

  private getForwardTarget(
    actionName: string,
    custom: string | undefined,
    forwardEditorPointer: PlatformContext['editorPointer'],
    forwardTargetContext: PlatformContext,
  ) {
    if (custom) {
      return this.getCustomActionTarget(custom, forwardEditorPointer);
    }

    if (isNativeActionName(actionName)) {
      return this.getNativeActionTarget(
        actionName,
        forwardEditorPointer,
        forwardTargetContext,
      );
    }

    return undefined;
  }

  private buildForwardEditorPointer(forwardPath: string[] | undefined) {
    if (!forwardPath) {
      return this.context.editorPointer;
    }

    return this.context.editorPointerAPI.buildEditorPointer(
      this.context.editorPointer.documentPointer,
      forwardPath,
    );
  }

  isExecutable(
    action: CustomManifestAction | HostEditActionDefinition,
  ): action is HostEditActionDefinition {
    return 'execute' in action;
  }

  getTarget(): {
    action:
      | CustomManifestAction
      | CustomizedManifestAction
      | HostEditActionDefinition;
    actionName: string;
    context: PlatformContext & {
      customisationAPI: ICustomisationAPI;
      actionPanelAPI: IActionPanelAPI;
    };
  } {
    this.validateAction();

    const { actionName, custom, elementPath } = this.action.execution.forward;
    const forwardInnerElementsPath =
      this.context.componentsAPI.innerElementPath.getPath(
        this.context.editorPointer ?? this.context.editorPointers[0],
        elementPath,
      );

    const forwardEditorPointer = this.buildForwardEditorPointer(
      forwardInnerElementsPath,
    );

    const isForwardedToSameAction =
      isEqual(this.context.editorPointer, forwardEditorPointer) &&
      this.actionName === custom;

    if (isForwardedToSameAction) {
      throw createEditorPlatformEditActionsError(
        EditorPlatformEditActionsErrorCode.EditActionForwardToSameAction,
        `Forward action cannot target itself for forwarded action: ${actionName}, custom: ${custom}`,
      ).withActionName(this.actionName);
    }

    const forwardTargetContext = {
      ...this.context,
      editorPointer: forwardEditorPointer,
      editorPointers: [forwardEditorPointer],
    };

    const forwardTarget = this.getForwardTarget(
      actionName,
      custom,
      forwardEditorPointer,
      forwardTargetContext,
    );

    if (!forwardTarget) {
      throw createEditorPlatformEditActionsError(
        EditorPlatformEditActionsErrorCode.EditActionForwardUnknownAction,
        `Forward action unknown target for forwarded action: ${actionName}, custom: ${custom}`,
      ).withActionName(this.actionName);
    }

    return {
      action: forwardTarget.action,
      actionName: forwardTarget.actionName,
      context: {
        ...forwardTargetContext,
        customisationAPI: createCustomisationAPI(
          forwardTarget.action,
          forwardTarget.actionName,
          forwardTargetContext,
        ),
        actionPanelAPI: createActionPanelAPI(
          forwardTarget.action,
          forwardTarget.actionName,
          forwardTargetContext,
        ),
      },
    };
  }
}
